/**
 * Browser detection and Safari-specific utilities
 */

export const isSafari = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  const userAgent = window.navigator.userAgent;
  const isSafariUA = /Safari/.test(userAgent) && !/Chrome/.test(userAgent) && !/Chromium/.test(userAgent);
  
  // Additional check for Safari on iOS
  const isIOSSafari = /iPad|iPhone|iPod/.test(userAgent) && /Safari/.test(userAgent);
  
  return isSafariUA || isIOSSafari;
};

export const isMobileSafari = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  const userAgent = window.navigator.userAgent;
  return /iPad|iPhone|iPod/.test(userAgent) && /Safari/.test(userAgent);
};

export const isIOS = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  return /iPad|iPhone|iPod/.test(window.navigator.userAgent);
};

/**
 * Safari-specific navigation helper to prevent redirect loops
 */
export const safariSafeNavigate = (navigate: (path: string, options?: any) => void, path: string, currentPath: string) => {
  // Prevent navigation if already on the target path
  if (currentPath === path) {
    return;
  }

  // Check for redirect loop before navigating
  if (isRedirectLoop(path)) {
    console.warn('Redirect loop detected, preventing navigation to:', path);
    return;
  }

  if (isSafari()) {
    // For Safari, use replace to prevent history issues and add longer delay
    setTimeout(() => {
      navigate(path, { replace: true });
    }, 200); // Longer delay for Safari to prevent rapid redirects
  } else {
    navigate(path, { replace: true });
  }
};

/**
 * Check if we're in a redirect loop scenario
 */
export const isRedirectLoop = (targetPath: string): boolean => {
  if (typeof window === 'undefined') return false;
  
  const currentPath = window.location.pathname;
  const redirectHistory = sessionStorage.getItem('redirect_history');
  
  if (!redirectHistory) {
    sessionStorage.setItem('redirect_history', JSON.stringify([currentPath]));
    return false;
  }
  
  try {
    const history: string[] = JSON.parse(redirectHistory);
    
    // Check if we've been to this path recently (within last 5 redirects)
    const recentRedirects = history.slice(-5);
    const redirectCount = recentRedirects.filter(path => path === targetPath).length;
    
    if (redirectCount >= 3) {
      console.warn('Potential redirect loop detected for path:', targetPath);
      return true;
    }
    
    // Add current path to history
    history.push(currentPath);
    
    // Keep only last 10 redirects
    if (history.length > 10) {
      history.splice(0, history.length - 10);
    }
    
    sessionStorage.setItem('redirect_history', JSON.stringify(history));
    return false;
  } catch (error) {
    console.error('Error checking redirect loop:', error);
    sessionStorage.removeItem('redirect_history');
    return false;
  }
};

/**
 * Clear redirect history (call this on successful navigation)
 */
export const clearRedirectHistory = (): void => {
  if (typeof window !== 'undefined') {
    sessionStorage.removeItem('redirect_history');
  }
};

/**
 * Safari-specific redirect loop prevention
 */
export const preventSafariRedirectLoop = (): boolean => {
  if (!isSafari()) return false;

  const lastRedirectTime = sessionStorage.getItem('last_redirect_time');
  const currentTime = Date.now();

  if (lastRedirectTime) {
    const timeDiff = currentTime - parseInt(lastRedirectTime);
    // If less than 1 second since last redirect, prevent it
    if (timeDiff < 1000) {
      console.warn('Safari redirect loop prevention: Too many redirects in short time');
      return true;
    }
  }

  sessionStorage.setItem('last_redirect_time', currentTime.toString());
  return false;
};

/**
 * Enhanced Safari-safe navigation with aggressive loop prevention
 */
export const enhancedSafariNavigate = (navigate: (path: string, options?: any) => void, path: string, currentPath: string) => {
  // Prevent navigation if already on the target path
  if (currentPath === path) {
    return false;
  }

  // Safari-specific redirect loop prevention
  if (preventSafariRedirectLoop()) {
    return false;
  }

  // Check for redirect loop before navigating
  if (isRedirectLoop(path)) {
    console.warn('Redirect loop detected, preventing navigation to:', path);
    return false;
  }

  if (isSafari()) {
    // For Safari, use replace to prevent history issues and add longer delay
    setTimeout(() => {
      navigate(path, { replace: true });
    }, 300); // Even longer delay for Safari
  } else {
    navigate(path, { replace: true });
  }

  return true;
};
